import {
    ACTION_TO_ADD_ANSWER,
    ACTION_TO_ADD_COMMENT,
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_COMMENT,
    ENTER_NEW_POSITION_OF_COMMENT,
    EXPLAIN_COMMENT_DELETION,
    FORUM_POST_PINNED,
    FORUM_POST_UNPINNED,
} from 'mk/autogenerated/translations/PostDetail.sagas.3c7aa581290c8a2da66677349bc5e563'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import {
    forum_api_comment_create_url,
    forum_api_delete_comment_url,
    forum_api_move_up_comment_url,
    forum_api_post_detail_url,
    forum_api_post_pin_url,
    forum_api_post_unpin_url,
} from 'mk/urls/functions';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import { COMMENT_CREATE_FORM_NAME } from 'mk2/apps/forum/components/CommentCreateEditForm';
import {
    CommentCreateNormalizedResponse,
    editableDisable,
    POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED,
    POST_DETAIL_COMMENT_CREATE_TRIGGER,
    POST_DETAIL_COMMENT_DELETE_TRIGGER,
    POST_DETAIL_COMMENT_REPLY,
    POST_DETAIL_FETCH_TRIGGER,
    POST_DETAIL_MOVE_UP_COMMENT,
    POST_DETAIL_POST_STICKY,
    POST_DETAIL_TRACK_TRIGGER,
    POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER,
    postDetailCommentCreateApi,
    PostDetailCommentCreateTriggerAction,
    postDetailCommentDeleteApi,
    PostDetailCommentDeleteNormalizedResponse,
    PostDetailCommentDeleteTriggerAction,
    PostDetailCommentReplyAction,
    postDetailFetchApi,
    PostDetailFetchNormalizedResponse,
    PostDetailFetchTriggerAction,
    PostDetailMoveUpCommentAction,
    postDetailMoveUpCommentApi,
    PostDetailPostStickyAction,
    postDetailPostStickyApi,
    PostDetailTrackTriggerAction,
    postDetailUpdateInBackgroundTrigger,
    PostDetailUpdateInBackgroundTriggerAction,
} from 'mk2/apps/forum/containers/PostDetail/PostDetail.actions';
import { getForumPostDetailState } from 'mk2/apps/forum/containers/PostDetail/PostDetail.selectors';
import {
    CampaignEntity,
    ContestEntity,
    ContestSchema,
    CounsellingEntity,
    MessageSchema,
    MicroPostEntity,
    MicroPostSchema,
    PostEntity,
    PostSchema,
} from 'mk2/apps/forum/schemas';
import * as forumUrls from 'mk2/apps/forum/urls';
import { getPageNumberInPost } from 'mk2/apps/forum/utils';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { toolbarCloseAll } from 'mk2/containers/Toolbar/Toolbar.actions';
import {
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
} from 'mk2/helpers/api';
import { autoFocusElement, handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { PhotoUploadStatus } from 'mk2/helpers/form.reducers';
import { scrollDocumentTo, scrollElementTo } from 'mk2/helpers/scrolling';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { tupdate } from 'mk2/helpers/types';
import { removeEmpty, removePage1, url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { AppState, getRequestDeviceIsIOS, getRequestDeviceMobile, getRequestUser } from 'mk2/reducers';
import { checkIfUserIsLoggedIn } from 'mk2/sagas/auth';
import { getDenormalizedEntity, getRoutingLocation } from 'mk2/selectors';
import { JAM_NOTIFICATION, JamNotificationAction } from 'mk2/services/jam.actions';
import { trackReach, TrackReachProduct } from 'mk2/trackReach';
import { normalize } from 'normalizr';
import { parse } from 'query-string';
import { replace } from 'react-router-redux';
import { change, formValueSelector } from 'redux-form';
import { delay } from 'redux-saga';
import { all, call, put, select, takeEvery, takeLatest, throttle } from 'redux-saga/effects';
import { isNumber } from 'util';

const logger = getLogger('forum/PostDetail.sagas');

interface ApiResponsePosts {
    body: {
        post: PostEntity;
        comments: CommentEntity[];
        page: number;
        numberOfPages: number;
        notificationsOn: number[];
        complaints: ComplaintEntity;
        commentsWithComplaint: CommentEntity[];
        contest: ContestEntity;
        contestAlreadyParticipated: boolean;
        nextPosts: MicroPostEntity[];
        similarPosts: MicroPostEntity[];
        recentPosts: MicroPostEntity[];
        counselling: CounsellingEntity;
        campaign: CampaignEntity;
    };
}

function* fetchPostDetail({ topicSlug, postSlug, pageRef, currentUrl, numCommentsBeforeLoad, xhr }: PostDetailFetchTriggerAction & XHRAction) {
    const query = parse(currentUrl.search);

    if (!pageRef) {
        // if pageRef is 0, negative or invalid string, we redirect us to page=1
        const gotoUrl = url(forumUrls.forumPostDetail, {topicSlug, postSlug}, removeEmpty(removePage1({...query, page: 1})));
        yield put(replace(gotoUrl));
        return;
    } else if (pageRef && query.page && query.page !== pageRef.toString()) {
        // if we succeed parsing incorrect string (e.g. page=3a), then redir to correct page (e.g. page=3)
        const gotoUrl = url(forumUrls.forumPostDetail, {topicSlug, postSlug}, removeEmpty(removePage1({...query, page: pageRef})));
        yield put(replace(gotoUrl));
        return;
    }

    if (numCommentsBeforeLoad > 0) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        if (query.scrollto === 'last') {
            scrollDocumentTo('bottom');
        } else if (query.scrollto === 'first') {
            scrollElementTo('#topPager', 'window-top', 50);
        }
    }

    yield put(postDetailFetchApi.request(topicSlug, postSlug, pageRef));

    try {
        const response: ApiResponsePosts = yield call(
            () => xhr.get(forum_api_post_detail_url(topicSlug, postSlug), {page: pageRef}),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako poces stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error('Wrong page');
        }

        const normalizedResponse: PostDetailFetchNormalizedResponse = normalize(response.body, {
            post: PostSchema,
            comments: [MessageSchema],
            complaints: [ComplaintSchema],
            commentsWithComplaint: [MessageSchema],
            contest: ContestSchema,
            nextPosts: [MicroPostSchema],
            similarPosts: [MicroPostSchema],
            recentPosts: [MicroPostSchema],
        });

        yield put(postDetailFetchApi.success(
            topicSlug, postSlug, pageRef,
            response.body.page,
            response.body.numberOfPages,
            response.body.notificationsOn,
            response.body.counselling,
            response.body.campaign,
            normalizedResponse,
        ));

        if (pageRef === 'last') {
            // replace page='last' with real page index
            const gotoUrl = url(forumUrls.forumPostDetail, {topicSlug, postSlug}, removeEmpty(removePage1({...query, page: response.body.page})));
            yield put(replace(gotoUrl));
        } else if (query.page === '1') {
            // remove ?page=1 from url
            const gotoUrl = url(forumUrls.forumPostDetail, {topicSlug, postSlug}, removeEmpty(removePage1({...query, page: 1})));
            yield put(replace(gotoUrl));
        }

        const numCommentsAfterLoad = response.body.comments ? response.body.comments.length : 0;
        if (numCommentsBeforeLoad !== numCommentsAfterLoad) {
            // other count of comments, either first load, or data on server changed, so scroll again
            if (query.scrollto === 'last') {
                scrollDocumentTo('bottom');
            } else if (query.scrollto === 'first') {
                scrollElementTo('#topPager', 'window-top', 50);
            }
        }

    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(postDetailFetchApi.failure(topicSlug, postSlug, pageRef, normalizeError(error)));
    }
}

function* updatePostDetailInBackground({ topicSlug, postSlug, pageRef, xhr }: PostDetailUpdateInBackgroundTriggerAction & XHRAction) {
    yield put(postDetailFetchApi.request(topicSlug, postSlug, pageRef));

    try {
        const response: ApiResponsePosts = yield call(
            () => xhr.get(forum_api_post_detail_url(topicSlug, postSlug), {page: pageRef}),
        );

        const normalizedResponse: PostDetailFetchNormalizedResponse = normalize(response.body, {
            post: PostSchema,
            comments: [MessageSchema],
            complaints: [ComplaintSchema],
            commentsWithComplaint: [MessageSchema],
            contest: ContestSchema,
            nextPosts: [MicroPostSchema],
            similarPosts: [MicroPostSchema],
            recentPosts: [MicroPostSchema],
        });

        yield put(postDetailFetchApi.success(
            topicSlug, postSlug, pageRef,
            response.body.page,
            response.body.numberOfPages,
            response.body.notificationsOn,
            response.body.counselling,
            response.body.campaign,
            normalizedResponse,
        ));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(postDetailFetchApi.failure(topicSlug, postSlug, pageRef, normalizeError(error)));
    }
}

export interface ApiResponseCommentCreate {
    body: {
        comment: CommentEntity;
        post: PostEntity;
        contest: ContestEntity;
        contestAlreadyParticipated: boolean;
        targetPostCommercialDetails: {
            /**
             * keep format in sync with export.py
             */
            relatedToDaysWith: string[],
            relatedToAmbassadorOfBrands: number[],
            relatedToTesting: string[],
        };
    };
}

function* commentCreate({ postId, commentFd, xhr }: PostDetailCommentCreateTriggerAction & XHRAction) {
    const isMobile = yield select(getRequestDeviceMobile);
    const isIOS = yield select(getRequestDeviceIsIOS);
    const post: PostEntity = yield select((state: AppState) => getDenormalizedEntity<PostEntity>(state, PostSchema, postId));
    const reducerKey = `${post.topic.slug}/${post.slug}`;
    const location = yield select(getRoutingLocation);
    const query = parse(location.search);
    const currentPage = parseInt(query.page, 10) || 1;

    yield put(postDetailCommentCreateApi.request(postId, commentFd, reducerKey));
    try {
        const response: ApiResponseCommentCreate = yield call(
            () => xhr.post(forum_api_comment_create_url(), {
                postId,
                commentData: tupdate(commentFd, {
                    photos: commentFd.photos ? commentFd.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS) : [],
                }),
            }),
        );

        const normalizedResponse: CommentCreateNormalizedResponse = normalize(response.body, {
            comment: MessageSchema,
            post: PostSchema,
            contest: ContestSchema,
        });

        // close toolbar, e.g. photos might be opened
        yield put(toolbarCloseAll(COMMENT_CREATE_FORM_NAME));

        const nextPage = getPageNumberInPost(response.body.comment);
        const gotoUrl = true || nextPage !== currentPage
            ? url(
                forumUrls.forumPostDetail,
                {topicSlug: response.body.post.topic.slug, postSlug: response.body.post.slug},
                removePage1(tupdate(query, {page: nextPage})),
                `comment-${response.body.comment.id}`,
            )
            // zostan na tej istej url
            : null;

        const details = response.body.targetPostCommercialDetails;
        if (details.relatedToDaysWith && details.relatedToDaysWith.length > 0) {
            details.relatedToDaysWith.forEach((item) => {
                trackReach(location, TrackReachProduct.DAYS_WITH, item, 'forum');
            });
        }
        if (details.relatedToAmbassadorOfBrands && details.relatedToAmbassadorOfBrands.length > 0) {
            details.relatedToDaysWith.forEach((item) => {
                trackReach(location, TrackReachProduct.AMBASSADOR, item, 'forum');
            });
        }
        if (details.relatedToTesting && details.relatedToTesting.length > 0) {
            details.relatedToDaysWith.forEach((item) => {
                trackReach(location, TrackReachProduct.TESTING, item, 'forum');
            });
        }

        yield handleXHRFormSuccessSaga(COMMENT_CREATE_FORM_NAME, gotoUrl);
        yield put(postDetailCommentCreateApi.success(postId, commentFd, reducerKey, normalizedResponse));

        // scroll to the bottom on mobile so user can see what his/her comment looks like
        if (isMobile && !gotoUrl && !isIOS) {
            scrollDocumentTo('bottom');
        }
    } catch (error) {
        yield handleXHRFormErrorSaga(COMMENT_CREATE_FORM_NAME, error as XHRFormError, logger);
        yield put(postDetailCommentCreateApi.failure(postId, commentFd, reducerKey, normalizeError(error)));
    }
}

function* commentReply({ username, textArea }: PostDetailCommentReplyAction) {
    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_ADD_ANSWER))) {
        return;
    }

    const isMobile = yield select(getRequestDeviceMobile);
    const isIOS = yield select(getRequestDeviceIsIOS);

    const text = yield select(formValueSelector(COMMENT_CREATE_FORM_NAME), 'text');
    const value = (text ? text + '\n' : '') + '@' + username + ' ';
    yield put(change(COMMENT_CREATE_FORM_NAME, 'text', value));

    if (!isMobile && !isIOS)  {
        // na firefoxe, edge nestaci zavolat len focus() na textarea,
        // (nenascrolluju tak, aby cela textarea bola viditelna)
        textArea.scrollIntoView(true);
    }
    autoFocusElement(textArea, 'end', textArea.value);
}

interface ApiResponseDeleteComment {
    body: {
        comment: CommentEntity,
    };
}

function* postDetailDeleteComment({ id, xhr }: PostDetailCommentDeleteTriggerAction & XHRAction) {
    const requestUser = yield select(getRequestUser);
    const comment = yield select((state: AppState) => getDenormalizedEntity<CommentEntity>(state, MessageSchema, id));
    const isAuthor = isAuthenticatedAs(requestUser, comment.authorUser);
    let reason;

    if (isAuthor) {
        if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_COMMENT)) {
            return;
        }
    } else { // admin or moderator
        reason = window.prompt(EXPLAIN_COMMENT_DELETION);
        if (reason === null) {
            return;
        }
    }

    yield put(postDetailCommentDeleteApi.request(id));

    try {
        const response: ApiResponseDeleteComment = yield call(
            () => xhr.post(forum_api_delete_comment_url(id), isAuthor ? {} : { comment: reason }),
        );

        const normalizedResponse: PostDetailCommentDeleteNormalizedResponse = normalize(response.body, {
            comment: MessageSchema,
        });
        yield put(postDetailCommentDeleteApi.success(id, normalizedResponse));

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(postDetailCommentDeleteApi.failure(id, normalizeError(error)));
    }
}

function* postDetailDisableEdit({itemId}: PostDetailTrackTriggerAction) {
    const query = parse(location.search);
    const currentPage = parseInt(query.page, 10) || 1;
    yield delay(121000);
    yield put(editableDisable(itemId, currentPage));
}

interface ApiResponseMoveUpComment {
    body: {
        comment: CommentEntity,
    };
}

function* postDetailMoveUpComment({ id, xhr }: PostDetailMoveUpCommentAction & XHRAction) {
    const newPosition = Number(window.prompt(ENTER_NEW_POSITION_OF_COMMENT));
    if (typeof newPosition !== 'number' && newPosition < 1) {
        return;
    }

    yield put(postDetailMoveUpCommentApi.request(id));

    try {
        const response: ApiResponseMoveUpComment = yield call(
            () => xhr.post(forum_api_move_up_comment_url(id), {newPosition}),
        );
        const normalizedResponse = normalize(response.body.comment, MessageSchema);
        yield put(postDetailMoveUpCommentApi.success(id, normalizedResponse));
        location.reload();

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(postDetailMoveUpCommentApi.failure(id, normalizeError(error)));
    }
}

function* postDetailJam({ channel }: JamNotificationAction) {
    // Fetch last page of the post
    const postId = parseFloat(channel.replace('forum:post:', ''));
    const post: PostEntity = yield select(getDenormalizedEntity, PostSchema, postId);
    if (!post) {
        return;
    }

    const postDetailState = yield select(getForumPostDetailState, post.topic.slug, post.slug);
    if (postDetailState.numberOfPages !== null) {
        yield put(postDetailUpdateInBackgroundTrigger(post.topic.slug, post.slug, postDetailState.numberOfPages));
    }
}

function* forumAddReplyUnauthenticated() { /* this saga runs only in case the user is not logged in */
    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_ADD_COMMENT))) {
        return;
    }
}

function* postSticky({ postId, sticky, xhr }: PostDetailPostStickyAction & XHRAction) {
    yield put(postDetailPostStickyApi.request(postId, sticky));

    try {
        const response = yield call(
            () => xhr.put(
                sticky
                    ? forum_api_post_pin_url(postId)
                    : forum_api_post_unpin_url(postId),
            ),
        );

        const normalizedResponse = normalize(response.body, {post: PostSchema});
        yield put(postDetailPostStickyApi.success(postId, sticky, normalizedResponse));

        // Then display toast
        yield showSuccessToast(sticky ? FORUM_POST_PINNED : FORUM_POST_UNPINNED);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(postDetailPostStickyApi.failure(postId, sticky, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(POST_DETAIL_FETCH_TRIGGER, fetchPostDetail),
        takeLatest(POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER, updatePostDetailInBackground),
        takeEvery(POST_DETAIL_COMMENT_CREATE_TRIGGER, commentCreate),
        takeLatest(POST_DETAIL_COMMENT_REPLY, commentReply),
        takeEvery(POST_DETAIL_COMMENT_DELETE_TRIGGER, postDetailDeleteComment),
        takeEvery(POST_DETAIL_MOVE_UP_COMMENT, postDetailMoveUpComment),
        takeEvery(POST_DETAIL_TRACK_TRIGGER, postDetailDisableEdit),
        takeEvery(POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED, forumAddReplyUnauthenticated),
        takeEvery(POST_DETAIL_POST_STICKY, postSticky),
        throttle(
            30 * 1000, // wait 30s for next reload
            (action: JamNotificationAction) => action.type === JAM_NOTIFICATION && action.channel.startsWith('forum:post:'),
            postDetailJam,
        ),
    ]);
}
